var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { WizardFormStep as RaWizardFormStep, } from '@react-admin/ra-form-layout';
import { usePermissions, useRecordContext, useResourceContext, } from 'react-admin';
import { canAccess } from '@react-admin/ra-rbac';
/**
 * Replacement for the default <WizardFormStep> that only renders a step if the user has the right permissions.
 * Use it with <WizardForm> from `@react-admin/ra-enterprise` to only display the steps the user has access to in the stepper.
 * Add a name prop to the WizardFormStep to define the sub-resource that the user needs to have the right permissions for.
 *
 * <WizardFormStep> also only renders the child inputs for which the user has the 'write' permissions.
 *
 * @example
 * import { Edit, TextInput } from 'react-admin';
 * import { WizardForm, WizardFormStep } from '@react-admin/ra-enterprise';
 *
 * const authProvider = {
 *     // ...
 *     getPermissions: () => Promise.resolve([
 *         { action: ['list', 'edit'], resource: 'products' },
 *         { action: 'write', resource: 'products.reference' },
 *         { action: 'write', resource: 'products.width' },
 *         { action: 'write', resource: 'products.height' },
 *         // 'products.description' is missing
 *         { action: 'write', resource: 'products.thumbnail' },
 *         // 'products.image' is missing
 *         { action: 'write', resource: 'products.step.description' },
 *         { action: 'write', resource: 'products.step.images' },
 *         // 'products.step.stock' is missing
 *     ]),
 * };
 *
 * const ProductCreate = () => (
 *     <Create>
 *         <WizardForm>
 *             <WizardForm.Step label="Description" name="description">
 *                 <TextInput source="reference" />
 *                 <TextInput source="width" />
 *                 <TextInput source="height" />
 *                 // not displayed
 *                 <TextInput source="description" />
 *             </WizardForm.Step>
 *             <WizardForm.Step label="Images" name="images">
 *                 // not displayed
 *                 <TextInput source="image" />
 *                 <TextInput source="thumbnail" />
 *             </WizardForm.Step>
 *             // not displayed
 *             <WizardForm.Step label="Stock" name="stock">
 *                 <TextInput source="stock" />
 *             </WizardForm.Step>
 *         </WizardForm>
 *     </Create>
 * );
 */
export var WizardFormStep = function (props) {
    var children = props.children, name = props.name, rest = __rest(props, ["children", "name"]);
    var resource = useResourceContext(props);
    var record = useRecordContext(props);
    var permissions = usePermissions().permissions;
    return (React.createElement(RaWizardFormStep, __assign({}, rest), React.Children.map(children, function (input) {
        return input &&
            canAccess({
                permissions: permissions,
                action: 'write',
                resource: "".concat(resource, ".").concat(input.props.source),
                record: record,
            })
            ? input
            : null;
    })));
};
